import { useEffect, useMemo, useState } from 'react'

import type { InternalRefetchQueriesInclude } from '@apollo/client'
import { useLazyQuery } from '@apollo/client'
import {
  ActionIcon,
  Collapse,
  Divider,
  Modal,
  Text,
  Tooltip,
  useModalStackContext,
} from '@mantine/core'
import {
  ChevronDown,
  ChevronRight,
  Download,
  FileCheck,
  FileClock,
  FileText,
  Plus,
} from 'lucide-react'
import { VerificationOfEmployment, Step } from 'types/graphql'

import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import DocumentPreview from 'src/components/Overlays/DocumentPreview/DocumentPreview'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_VOE_ACTIONS,
  GET_VOE_FILE_DOWNLOAD_URL,
} from 'src/graphql/voe.graphql'
import UploadDocumentModal from 'src/pages/ApplicantDetailsPage/tabs/DocumentsTab/components/UploadDocumentModal/UploadDocumentModal'

interface VoEDocumentsSectionProps {
  voe: VerificationOfEmployment
  applicantId: string
  /**
   * The drawer's refetch list: operation names and `{ query, variables }`
   * entries (VoETab passes the latter), so not just strings.
   */
  onRender?: InternalRefetchQueriesInclude
}

const PREVIEW_STACK_ID = 'voe-doc-preview-modal'

export type VoEAttachmentKind =
  | 'RESPONSE'
  | 'REQUEST'
  | 'CONSENT'
  | 'ATS'
  | 'AUDIT_TRAIL'
  | 'UPLOAD'

const KIND_ORDER: VoEAttachmentKind[] = [
  'RESPONSE',
  'REQUEST',
  'CONSENT',
  'ATS',
  'AUDIT_TRAIL',
  'UPLOAD',
]

const KIND_LABEL: Record<VoEAttachmentKind, string> = {
  RESPONSE: 'Response',
  REQUEST: 'Request',
  CONSENT: 'Consent',
  ATS: 'ATS',
  AUDIT_TRAIL: 'Audit trail',
  UPLOAD: 'Upload',
}

const kindOfDocument = (
  voeDocumentType: string | null | undefined
): VoEAttachmentKind => {
  switch (voeDocumentType) {
    case 'RESPONSE_PDF':
      return 'RESPONSE'
    case 'REQUEST_PDF':
      return 'REQUEST'
    case 'CONSENT_PDF':
      return 'CONSENT'
    case 'ATS_REQUEST_CONFIRMATION':
    case 'ATS_RESPONSE_NOTIFICATION':
      return 'ATS'
    case 'AUDIT_TRAIL_PDF':
      return 'AUDIT_TRAIL'
    default:
      return 'UPLOAD'
  }
}

export interface VoEAttachment {
  documentId: string
  fileName?: string | null
  kind: VoEAttachmentKind
  pendingReview?: boolean
  isUrlFile?: boolean
  fileUrl?: string | null
  onClickOverride?: () => void
}

const DocumentPreviewModal = ({
  document,
  applicantId,
  onClose,
  refetchQueries,
}: {
  document: { documentId: string }
  applicantId: string
  onClose: () => void
  /** Completing a linked document completes the VoE, so the drawer reloads. */
  refetchQueries?: InternalRefetchQueriesInclude
}) => {
  const stackCtx = useModalStackContext()

  // Mantine gap: Modal's stack-register useEffect has no cleanup, so
  // unmounting while opened leaks the stackId in Modal.Stack. This child
  // component's lifecycle matches the conditional Modal mount/unmount, so
  // the cleanup fires on the actual close path. Mirrors DeleteModal.
  useEffect(() => {
    return () => {
      if (stackCtx) {
        stackCtx.removeModal(PREVIEW_STACK_ID)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Modal
      size="90rem"
      lockScroll={false}
      stackId={PREVIEW_STACK_ID}
      classNames={{
        content: 'min-h-96 h-[100%]',
        body: 'flex h-[95%] flex-col overflow-auto',
      }}
      transitionProps={{ transition: 'fade', duration: 200 }}
      opened
      onClose={onClose}
      withCloseButton={false}
    >
      <DocumentPreview
        documentId={document.documentId}
        applicantId={applicantId}
        showClosebutton={true}
        showBackButton={false}
        onBack={onClose}
        showActions={false}
        refetchQueries={Array.isArray(refetchQueries) ? refetchQueries : []}
      />
    </Modal>
  )
}

const VoEDocumentsSection = ({
  voe,
  applicantId,
  onRender,
}: VoEDocumentsSectionProps) => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)

  // Uploading the employer's response completes the VoE and cancels its
  // pending outreach, so refetch the scheduled actions along with the drawer's
  // VoE (status, audit trail, Response tag).
  const uploadRefetchQueries = useMemo<InternalRefetchQueriesInclude>(
    () => [...(Array.isArray(onRender) ? onRender : []), GET_VOE_ACTIONS],
    [onRender]
  )

  const [fetchDownloadUrl] = useLazyQuery(GET_VOE_FILE_DOWNLOAD_URL, {
    fetchPolicy: 'network-only',
  })

  const handleOpenUploadModal = () => {
    setIsUploadModalOpen(true)
  }

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false)
  }

  const handleVoeFileClick = async (fileType: 'request' | 'response') => {
    try {
      const { data } = await fetchDownloadUrl({
        variables: { voeId: voe.voeId, fileType },
      })
      if (data?.voeFileDownloadUrl) {
        window.open(data.voeFileDownloadUrl, '_blank')
      } else {
        toast('File not available', 'error')
      }
    } catch (error) {
      toast('Error downloading file', 'error')
    }
  }

  // Every file the drawer can show, sorted so the employer's answers come
  // first: a recruiter's uploaded reply, the portal response PDF or the
  // legacy S3 response, reviewed ones before those still pending. Then the
  // request, the consent, ATS notifications and anything else uploaded.
  // Document records win over S3 links for the request and response PDFs.
  const attachments = useMemo<VoEAttachment[]>(() => {
    const result: VoEAttachment[] = []
    const addedDocumentIds = new Set<string>()

    if (voe.document?.documentId) {
      result.push({
        ...voe.document,
        kind: 'RESPONSE',
        pendingReview: voe.document.userStatus === 'PENDING_REVIEW',
      })
      addedDocumentIds.add(voe.document.documentId)
    }

    const requestDoc = voe.documents?.find(
      (d) => d.voeDocumentType === 'REQUEST_PDF'
    )
    const responseDoc = voe.documents?.find(
      (d) => d.voeDocumentType === 'RESPONSE_PDF'
    )

    if (voe.driverApplicationConsentDocument?.documentId) {
      result.push({ ...voe.driverApplicationConsentDocument, kind: 'CONSENT' })
      addedDocumentIds.add(voe.driverApplicationConsentDocument.documentId)
    }

    if (requestDoc) {
      result.push({ ...requestDoc, kind: 'REQUEST' })
      addedDocumentIds.add(requestDoc.documentId)
    } else if (voe.hasRequestFile || voe.safetyRequestConsentFileUrl) {
      result.push({
        documentId: `request-${voe.voeId}`,
        fileName: voe.companyName
          ? `${voe.companyName} - Safety Performance History Records Request`
          : 'Safety Performance History Records Request',
        kind: 'REQUEST',
        isUrlFile: true,
        onClickOverride: () => handleVoeFileClick('request'),
      })
    }

    if (responseDoc) {
      if (!addedDocumentIds.has(responseDoc.documentId)) {
        result.push({ ...responseDoc, kind: 'RESPONSE' })
        addedDocumentIds.add(responseDoc.documentId)
      }
    } else if (voe.hasResponseFile || voe.downloadResponseFileUrl) {
      result.push({
        documentId: `response-${voe.voeId}`,
        fileName: voe.companyName
          ? `${voe.companyName} - Safety Performance History Records Response`
          : 'Safety Performance History Records Response',
        kind: 'RESPONSE',
        isUrlFile: true,
        onClickOverride: () => handleVoeFileClick('response'),
      })
    }

    if (voe.documents) {
      for (const doc of voe.documents) {
        if (!addedDocumentIds.has(doc.documentId)) {
          result.push({ ...doc, kind: kindOfDocument(doc.voeDocumentType) })
        }
      }
    }

    return result
      .map((attachment, index) => ({ attachment, index }))
      .sort(
        (a, b) =>
          KIND_ORDER.indexOf(a.attachment.kind) -
            KIND_ORDER.indexOf(b.attachment.kind) ||
          Number(Boolean(a.attachment.pendingReview)) -
            Number(Boolean(b.attachment.pendingReview)) ||
          a.index - b.index
      )
      .map(({ attachment }) => attachment)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    voe.voeId,
    voe.document?.documentId,
    voe.document?.userStatus,
    voe.driverApplicationConsentDocument?.documentId,
    voe.documents,
    voe.hasRequestFile,
    voe.hasResponseFile,
    voe.safetyRequestConsentFileUrl,
    voe.downloadResponseFileUrl,
  ])

  const responseAttachments = attachments.filter((a) => a.kind === 'RESPONSE')
  const hasResponse = responseAttachments.length > 0
  const responsePending =
    hasResponse && responseAttachments.every((a) => a.pendingReview)

  // Start collapsed to keep the drawer short; the header shows the count and status
  const [isExpanded, setIsExpanded] = useState(false)
  const [documentForPreview, setDocumentForPreview] = useState(null)

  const handlePreview = (doc: VoEAttachment) => {
    if (doc.onClickOverride) {
      doc.onClickOverride()
      return
    }
    if (doc.isUrlFile && doc.fileUrl) {
      window.open(doc.fileUrl, '_blank')
      return
    }
    setDocumentForPreview(doc)
  }

  return (
    <>
      {documentForPreview && (
        <DocumentPreviewModal
          document={documentForPreview}
          applicantId={applicantId}
          onClose={() => setDocumentForPreview(null)}
          refetchQueries={uploadRefetchQueries}
        />
      )}

      {/* Documents Section */}
      <div className="flex items-center justify-between">
        <button
          className="flex items-center gap-1 text-doubleNickel-gray-500 hover:text-doubleNickel-gray-700"
          onClick={() => setIsExpanded((v) => !v)}
        >
          {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          <Text size="xs" fw={400} className="text-inherit">
            DOCUMENTS
            <span className="ml-1 text-doubleNickel-gray-400">
              ({attachments.length})
            </span>
            {hasResponse && !responsePending && (
              <span className="ml-2 font-medium text-doubleNickel-success-600">
                Response received
              </span>
            )}
          </Text>
        </button>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          onClick={handleOpenUploadModal}
          className="h-6 w-6 hover:bg-doubleNickel-gray-100"
        >
          <Plus size={14} className="stroke-doubleNickel-gray-600" />
        </ActionIcon>
      </div>

      <Collapse in={isExpanded}>
        {attachments.length > 0 ? (
          <div className="flex flex-col gap-1">
            {attachments.map((attachment) => {
              const isResponse = attachment.kind === 'RESPONSE'
              const Icon = attachment.pendingReview
                ? FileClock
                : isResponse
                ? FileCheck
                : attachment.isUrlFile
                ? Download
                : FileText
              const iconClass = attachment.pendingReview
                ? 'text-doubleNickel-warning-500'
                : isResponse
                ? 'text-doubleNickel-success-600'
                : 'text-doubleNickel-gray-400'
              return (
                <Tooltip
                  label={attachment.fileName}
                  key={attachment.documentId}
                  position="top"
                  openDelay={300}
                >
                  <button
                    data-testid={`voe-document-${attachment.kind.toLowerCase()}`}
                    className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-doubleNickel-gray-50 ${
                      isResponse
                        ? 'bg-doubleNickel-gray-25 font-medium text-doubleNickel-gray-900'
                        : 'text-doubleNickel-gray-700'
                    }`}
                    onClick={() => handlePreview(attachment)}
                  >
                    <Icon
                      size={14}
                      strokeWidth={1.5}
                      className={`flex-shrink-0 ${iconClass}`}
                    />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate">{attachment.fileName}</span>
                      {attachment.pendingReview && (
                        <span className="text-[10px] font-normal text-doubleNickel-gray-500">
                          Open it to review, then mark the verification complete
                        </span>
                      )}
                    </span>
                    <StyledBadge
                      compact="xs"
                      color={
                        attachment.pendingReview
                          ? 'orange'
                          : isResponse
                          ? 'green'
                          : 'gray'
                      }
                      wrapperClassName="flex-shrink-0"
                    >
                      {attachment.pendingReview
                        ? 'Pending review'
                        : KIND_LABEL[attachment.kind]}
                    </StyledBadge>
                  </button>
                </Tooltip>
              )
            })}
          </div>
        ) : (
          <Text
            size="xs"
            className="py-1 text-center text-doubleNickel-gray-400"
          >
            No documents
          </Text>
        )}
      </Collapse>

      <UploadDocumentModal
        applicantId={applicantId}
        isOpen={isUploadModalOpen}
        onClose={handleCloseUploadModal}
        onRender={uploadRefetchQueries}
        step={null as Step}
        voe={voe}
        showExpirationDate={false}
      />

      <Divider variant="dashed" />
    </>
  )
}

export default VoEDocumentsSection
